import { frameCacheKey, useFrameVersion } from './artifact-updated';

/**
 * FRONT-2594 — the full-size viewer for a sketch or a slide deck: an iframe on
 * the item's own `/apps/<slug>/` page.
 *
 * Sandbox attributes are copied from the thumbnails that already show these
 * same pages, not invented: a sketch is model-authored HTML with no need for
 * cookies or the SDK, so `allow-scripts` only (sketch-card.tsx); a deck's
 * thumbnail (app-preview.tsx, `source="live"`) also lists `allow-same-origin`,
 * and so does this frame. That token does not give a deck its own origin here:
 * both kinds are served with a CSP `sandbox allow-scripts` (src/apps/display-csp.ts),
 * and a CSP sandbox overrides the iframe attribute, so the framed deck is on an
 * opaque origin like a sketch. A deck's web fonts load through the CSP's font-host
 * allowlist, not through origin access.
 */
export function canvasFrameSrc(slug: string, cacheKey: string | number, path?: string | null): string {
  const base = path || `/apps/${encodeURIComponent(slug)}/`;
  return `${base}${base.includes('?') ? '&' : '?'}v=${encodeURIComponent(String(cacheKey))}`;
}

export function canvasFrameSandbox(kind: 'sketch' | 'deck'): string {
  return kind === 'deck' ? 'allow-scripts allow-same-origin' : 'allow-scripts';
}

/**
 * FRONT-2744 — a deck's own F key needs the frame to be allowed to go
 * fullscreen; a sketch has no such control. Only `allowFullScreen`, never
 * `allow="fullscreen"`: the deck's CSP sandbox puts it on an opaque origin, and
 * `allow="fullscreen"` grants the frame's SOURCE origin only, overriding
 * `allowFullScreen` — measured in headless Chromium, that pair is refused
 * ("Disallowed by permissions policy") while `allowFullScreen` alone works.
 */
export function canvasFrameAllowsFullscreen(kind: 'sketch' | 'deck'): boolean {
  return kind === 'deck';
}

export function CanvasFrame({
  kind,
  slug,
  title,
  cacheKey,
  path,
}: {
  kind: 'sketch' | 'deck';
  slug: string;
  title: string;
  /** `updatedAt` when the tool output carries one, else the item's revision. */
  cacheKey: string | number;
  /** The tool output's own served path (sketches), else `/apps/<slug>/`. */
  path?: string | null;
}) {
  // An `artifact-updated` event's version joins the key, so Eddy's revision reloads the frame.
  const announced = useFrameVersion(kind, slug);
  return (
    <iframe
      data-testid="canvas-frame"
      src={canvasFrameSrc(slug, frameCacheKey(cacheKey, announced), path)}
      title={title}
      sandbox={canvasFrameSandbox(kind)}
      allowFullScreen={canvasFrameAllowsFullscreen(kind)}
      className="w-full flex-1 border-0 bg-canvas-surface"
    />
  );
}
